import React, {
    Fragment,
    RefObject,
    useCallback,
    useEffect,
    useState,
} from "react";
import {
    POST_LIST_TYPES,
    PostComponentOptionsType,
    PostType,
} from "./Post.types";
import SkeletonPost from "./SkeletonPost";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { SHOW_AVATAR_MODAL_EVENT } from "../Modals/ServiceModals/AvatarModal";
import { PostOptionsModal } from "../Modals/ServiceModals/PostOptionsModal/PostOptionsModal";
import CommentOptionsModal from "../Modals/ServiceModals/CommentOptionsModal/CommentOptionsModal";
import { CommentOptionsModalType } from "../Modals/ServiceModals/ServiceModal.types";
import { CommentType } from "../Comment/CommentTypes";
import { SERVICE_MODALS_OPTIONS_STATES } from "../Modals/ServiceFunctionsModal.types";
import { Box } from "@mui/material";
import {
    fetcherReactToComment,
    fetcherUnReactToComment,
} from "../../DataFetchers/CommentDataFetcher";
import {
    useInterruptableAction,
    UserLoginActionState,
    useUserStore,
} from "@infrastructure/Persistence/UserState";
import {
    RESPONSE_CODE_LIMIT_EXCEEDED,
    RESPONSE_CODE_PROFILE_SUSPENDED,
    RESPONSE_CODE_RESOURCE_NOT_FOUND,
    RESPONSE_CODE_RESOURCE_SUSPENDED,
} from "@infrastructure/BackendClientCodes";
import {
    ErrorState,
    useErrorStore,
} from "../Modals/ServiceModals/GeneralErrorsModal/GlobalState";
import { AxiosError } from "axios";
import {
    fetcherReactToPost,
    fetcherRemoveReactFromPost,
} from "../../DataFetchers/PostDataFetcher";
import { ReactionResponse } from "@shared/Types/Reactions/ReactionTypes.types";
import {
    ReactionActionState,
    SWITCHING_STATE_TO_IDLE_TIME,
    useReactionActionStore,
} from "../ReactionsTooltip/ReactionActionState";
import { LocationType } from "../SearchHeader/ListItem/ListItemLocation.types";
import { UserType } from "@shared/types";
import { usePostDetailsNavigationActions } from "@infrastructure/Hooks/usePostDetailsNavigationActions";
import { POST_SOURCE_TYPE } from "./PostForm/PostFormModal.types";
import { CommentFormModeEnum } from "../Modals/CommentFormModal/CommentFormModalOpenState";
import {
    addSubscriber,
    publish,
    removeSubscriber,
} from "@infrastructure/Events/EventBus";
import { PostDetailsPageActions } from "../Modals/PostDetailsModal/PostDetailsModal";
import { ReactJSXElement } from "@emotion/react/types/jsx-namespace";
import { ManualLoadingInfiniteScroller } from "./ManualLoadingInfiniteScroller";
import {
    RESET_API_CACHE_REASONS,
    resetIgnoreApiCacheTime,
} from "@infrastructure/Services/ApiUtility";
import { VideoSwiperContainer } from "./PreviewVideosSwiper/VideoSwiperContainer";
import { PostDetailsActionType } from "../Modals/PostDetailsModal/PostDetailsModalStore";
import { MediaSwiperContainer } from "../MediaListSwiper/MediaSwiperContainer";
import { POST_TYPES } from "@infrastructure/Services/PostService.types";
import {
    addUserReactionToObject,
    deleteUserReactionOnObject,
    updateUserReactionOnObject,
} from "@infrastructure/Hooks/UserInteractions/useUserReactions";
import { ReactionableObjectEnum } from "@infrastructure/Hooks/UserInteractions/UserInteractions.types";
import {
    POLL_RESPONSE_SUBMIT_ERROR,
    POLL_RESPONSE_SUBMITTED,
    POST_DETAILS_CHANGED,
    POST_OPTIONS_CLICK,
} from "./PostForm/events";
import { usePostComponentFactory } from "@infrastructure/Hooks/Post/usePostComponentFactory";
import { useOnPostReaction } from "../Reactions/Hooks/useOnPostReaction";
import { OnReactionEvent } from "../Reactions/Events/OnReactionEvent.types";

export enum ROOT_MARGIN {
    LAZY = "0px 0px 300px 0px",
    SEMI_LAZY = "0px 0px 1000px 0px",
    EAGER = "0px 0px 3000px 0px",
}

export function PostsWrapper({
    posts,
    openPostFormHandler,
    openPostDetailsPage,
    openCommentInputModal,
    onPostEdit,
    isInfiniteScrollEnabled = false,
    dataLoader,
    isLoading = false,
    hasNextPage,
    error,
    isInitLoadingDone,
    updatePostFromServer,
    showLoadMoreButton = false,
    showGroupCoverImage = false,
    autoLoadDataAfterClick = true,
    listType,
    loadingMoreButtonText = "",
    updatePostComponent,
    setInitialPostLocation,
    setHeaderComponent,
    onPostCardClick,
    handleCloseForHeaderComponent,
    autoPlayVideo,
    groupId,
    scrollableContainerRef,
}: {
    posts: PostType[];
    openPostFormHandler?: (sourceType?: POST_SOURCE_TYPE) => void;
    openPostDetailsPage?: (
        e: React.MouseEvent<HTMLElement>,
        id: number,
    ) => void;
    openCommentInputModal?: (modalMode: CommentFormModeEnum) => void;
    onPostEdit?: (post: PostType) => void;
    showShareOption?: boolean;
    isInfiniteScrollEnabled?: boolean;
    autoLoadDataAfterClick?: boolean;
    dataLoader: () => void;
    isLoading?: boolean;
    hasNextPage: boolean;
    error?: unknown;
    isInitLoadingDone?: boolean;
    updatePostFromServer?: (postId: number) => void;
    showLoadMoreButton: boolean;
    showGroupCoverImage: boolean;
    listType: POST_LIST_TYPES;
    loadingMoreButtonText?: string;
    updatePostComponent?: (postFetched: PostType) => void;
    setInitialPostLocation?: (location: LocationType | undefined) => void;
    pageCount?: number;
    page?: number;
    setPage?: React.Dispatch<React.SetStateAction<number>>;
    setHeaderComponent?: (component: ReactJSXElement) => void;
    onPostCardClick?: (postId: string, action?: PostDetailsActionType) => void;
    handleCloseForHeaderComponent?: () => void;
    groupId?: string;
    autoPlayVideo?: boolean;
    scrollableContainerRef?: RefObject<HTMLDivElement> | undefined;
}) {
    const [postList, setPostList] = useState<PostType[]>(posts);

    useEffect(() => {
        setPostList(posts);
    }, [posts]);

    const [infiniteRef, { rootRef }] = useInfiniteScroll({
        loading: isLoading,
        hasNextPage,
        onLoadMore: dataLoader,
        disabled: !!error || !isInfiniteScrollEnabled,
        rootMargin: ROOT_MARGIN.SEMI_LAZY,
    });
    const { setErrorState } = useErrorStore();
    const { setReactionActionState } = useReactionActionStore();

    useEffect(() => {
        if (scrollableContainerRef) {
            rootRef(scrollableContainerRef.current);
        }
    }, [scrollableContainerRef]);

    const [isPostOptionsModalOpen, setIsPostOptionsModalOpen] = useState(false);
    const [modalCurrentAction, setModalCurrentAction] = useState(
        SERVICE_MODALS_OPTIONS_STATES.POST_STARTING_OPTIONS,
    );
    const [selectedPost, setSelectedPost] =
        useState<
            Pick<PostComponentOptionsType, "id" | "data" | "postActions">
        >();

    useEffect(() => {
        const subscriber = addSubscriber(POST_OPTIONS_CLICK, (event) => {
            setSelectedPost(event.body.post);
            setIsPostOptionsModalOpen(true);
            setModalCurrentAction(event.body.modalState);
        });

        return () => removeSubscriber(subscriber);
    }, []);

    const [selectedComment, setSelectedComment] = useState({} as CommentType);
    const [openCommentOptionsModal, setOpenCommentOptionsModal] =
        useState(false);

    const { navigateToPostWithAction } = usePostDetailsNavigationActions();

    function handleReplyPreviewComment(comment: CommentType) {
        navigateToPostWithAction(
            comment.postId,
            PostDetailsPageActions.REPLY,
            comment.commentContent.id,
        );
    }

    function handleEditingPreviewComment(comment: CommentType) {
        navigateToPostWithAction(
            comment.postId,
            PostDetailsPageActions.EDIT,
            comment.commentContent.id,
        );
    }

    function updateCommentPost(postId: number) {
        updatePostFromServer?.(postId);
    }

    const [
        commentOptionsModalCurrentAction,
        setCommentOptionsModalCurrentAction,
    ] = useState(SERVICE_MODALS_OPTIONS_STATES.COMMENT_STARTING_OPTIONS);

    const [postActionInProgress, setPostActionInProgress] = useState<any>();
    const [commentActionInProgress, setCommentActionInProgress] =
        useState<any>();
    const { setUserAuthContextState } = useUserStore();
    const createReactOnPreviewComment =
        (commentId: string, postId: number) =>
        (reactionTypeName: string, action: string) => {
            resetIgnoreApiCacheTime(
                RESET_API_CACHE_REASONS.REACT_ON_PREVIEW_COMMENT,
            );
            setReactionActionState(ReactionActionState.LOADING);
            setCommentActionInProgress({
                reactionTypeName: reactionTypeName,
                action: action,
                commentId: commentId,
                postId: postId,
            });
            setUserAuthContextState(UserLoginActionState.CREATE_REACTION);
            performReactionOnComment(
                reactionTypeName,
                action,
                commentId,
                postId,
            );
        };

    const performReactionOnComment = (
        reactionTypeName: string,
        action: string,
        commentId: string,
        postId: number,
    ) => {
        const catchError = (err: AxiosError) => {
            switch (err.response?.status) {
                case RESPONSE_CODE_RESOURCE_SUSPENDED:
                case RESPONSE_CODE_RESOURCE_NOT_FOUND:
                    setErrorState(ErrorState.COMMENT_SUSPENDED);
                    break;
                case RESPONSE_CODE_PROFILE_SUSPENDED:
                    setErrorState(ErrorState.USER_INACTIVE_OR_SUSPENDED);
                    break;
                case RESPONSE_CODE_LIMIT_EXCEEDED:
                    setErrorState(ErrorState.LIMIT_EXCEEDED_ERROR);
                    break;
            }
            setTimeout(
                () => setReactionActionState(ReactionActionState.IDLE),
                SWITCHING_STATE_TO_IDLE_TIME,
            );
        };

        switch (action) {
            case "create":
                fetcherReactToComment(commentId, reactionTypeName)
                    .then(() => {
                        updateCommentPost(postId);
                        addUserReactionToObject(
                            commentId,
                            reactionTypeName,
                            ReactionableObjectEnum.COMMENT,
                        );
                        setCommentActionInProgress(undefined);
                        setTimeout(
                            () =>
                                setReactionActionState(
                                    ReactionActionState.IDLE,
                                ),
                            SWITCHING_STATE_TO_IDLE_TIME,
                        );
                    })
                    .catch(catchError);
                break;

            case "delete":
                fetcherUnReactToComment(commentId, reactionTypeName)
                    .then(() => {
                        updateCommentPost(postId);
                        deleteUserReactionOnObject(
                            commentId,
                            ReactionableObjectEnum.COMMENT,
                        );
                        setCommentActionInProgress(undefined);
                        setTimeout(
                            () =>
                                setReactionActionState(
                                    ReactionActionState.IDLE,
                                ),
                            SWITCHING_STATE_TO_IDLE_TIME,
                        );
                    })
                    .catch(catchError);
                break;
        }
    };

    useInterruptableAction(commentActionInProgress, () =>
        performReactionOnComment(
            commentActionInProgress.reactionTypeName,
            commentActionInProgress.action,
            commentActionInProgress.commentId,
            commentActionInProgress.postId,
        ),
    );

    useEffect(() => {
        const pollResponseSubscriber = addSubscriber(
            POLL_RESPONSE_SUBMITTED,
            (event) => {
                const postId = event.body.data.postId;
                const postIndex = postList.findIndex(
                    (item: PostType) =>
                        item.id.toString() === postId.toString(),
                );
                if (postIndex === -1) {
                    return;
                }
                updatePostFromServer?.(postId);
            },
        );
        const pollErrorSubscriber = addSubscriber(
            POLL_RESPONSE_SUBMIT_ERROR,
            (event) => {
                const postIndex = postList.findIndex(
                    (item: PostType) =>
                        item.id.toString() ===
                        event.body.data.postId.toString(),
                );
                if (postIndex === -1) {
                    return;
                }

                const err = event.body.data.err;
                switch (err.response?.status) {
                    case RESPONSE_CODE_RESOURCE_SUSPENDED:
                    case RESPONSE_CODE_RESOURCE_NOT_FOUND:
                        setErrorState(ErrorState.POST_SUSPENDED);
                        break;
                    case RESPONSE_CODE_PROFILE_SUSPENDED:
                        setErrorState(ErrorState.USER_INACTIVE_OR_SUSPENDED);
                        break;
                    case RESPONSE_CODE_LIMIT_EXCEEDED:
                        setErrorState(ErrorState.LIMIT_EXCEEDED_ERROR);
                        break;
                    default:
                        break;
                }
            },
        );
        return () => {
            removeSubscriber(pollResponseSubscriber);
            removeSubscriber(pollErrorSubscriber);
        };
    }, [postList, updatePostFromServer]);

    const commentOptionsModalProps: CommentOptionsModalType = {
        open: openCommentOptionsModal,
        setOpen: setOpenCommentOptionsModal,
        selectedComment: selectedComment,
        onCommentReplyOptionClickHandler: handleReplyPreviewComment,
        handleEditingComment: handleEditingPreviewComment,
        updateCommentPost: updateCommentPost,
        setModalCurrentAction: setCommentOptionsModalCurrentAction,
        modalCurrentAction: commentOptionsModalCurrentAction,
        createReactOnPreviewComment: createReactOnPreviewComment,
    };

    function handleCommentPreviewOptionsClicked(comment: CommentType) {
        setSelectedComment(comment);
        setCommentOptionsModalCurrentAction(
            SERVICE_MODALS_OPTIONS_STATES.COMMENT_PREVIEW_STARTING_OPTIONS,
        );
        setOpenCommentOptionsModal(true);
    }

    function handleCommentPreviewReportClicked(comment: CommentType) {
        setSelectedComment(comment);
        setCommentOptionsModalCurrentAction(
            SERVICE_MODALS_OPTIONS_STATES.REPORTING_CONTENT,
        );
        setOpenCommentOptionsModal(true);
    }

    const onPostReaction = useCallback(
        (event: OnReactionEvent) => {
            updateUserReactionOnObject(
                event.entityId.toString(),
                event.reactions,
                ReactionableObjectEnum.POST,
            );
            setTimeout(
                () => setReactionActionState(ReactionActionState.IDLE),
                SWITCHING_STATE_TO_IDLE_TIME,
            );
        },
        [setReactionActionState],
    );

    useOnPostReaction(setPostList, onPostReaction);

    const createReactOnPost =
        (postId: number) => (reactionTypeName: string, action: string) => {
            resetIgnoreApiCacheTime(RESET_API_CACHE_REASONS.REACT_ON_POST);
            setReactionActionState(ReactionActionState.LOADING);
            setPostActionInProgress({
                reactionTypeName: reactionTypeName,
                action: action,
                postId: postId,
            });
            performReactionOnPost(reactionTypeName, action, postId);
        };

    const performReactionOnPost = (
        reactionTypeName: string,
        action: string,
        postId: number,
    ) => {
        const updatedPosts = JSON.parse(JSON.stringify(postList));
        const postIndex = updatedPosts.findIndex(
            (item: PostType) => item.id.toString() === postId.toString(),
        );
        if (postIndex === -1) {
            return;
        }
        const catchError = (err: AxiosError) => {
            switch (err.response?.status) {
                case RESPONSE_CODE_RESOURCE_SUSPENDED:
                case RESPONSE_CODE_RESOURCE_NOT_FOUND:
                    setErrorState(ErrorState.POST_SUSPENDED);
                    break;
                case RESPONSE_CODE_PROFILE_SUSPENDED:
                    setErrorState(ErrorState.USER_INACTIVE_OR_SUSPENDED);
                    break;
                case RESPONSE_CODE_LIMIT_EXCEEDED:
                    setErrorState(ErrorState.LIMIT_EXCEEDED_ERROR);
                    break;
            }
            setTimeout(
                () => setReactionActionState(ReactionActionState.IDLE),
                SWITCHING_STATE_TO_IDLE_TIME,
            );
        };
        let reactionFetcher;

        switch (action) {
            case "create":
                reactionFetcher = fetcherReactToPost;
                break;
            case "delete":
                reactionFetcher = fetcherRemoveReactFromPost;
                break;
            default:
                return;
        }
        reactionFetcher(postId, reactionTypeName)
            .then((res: ReactionResponse) => {
                if (action === "create") {
                    addUserReactionToObject(
                        postId.toString(),
                        reactionTypeName,
                        ReactionableObjectEnum.POST,
                    );
                } else {
                    deleteUserReactionOnObject(
                        postId.toString(),
                        ReactionableObjectEnum.POST,
                    );
                }
                updatedPosts[postIndex].postData.postReactions = res.reactions;

                updatePostComponent?.(updatedPosts[postIndex]);

                setPostActionInProgress(undefined);
                setTimeout(
                    () => setReactionActionState(ReactionActionState.IDLE),
                    SWITCHING_STATE_TO_IDLE_TIME,
                );
                publish({
                    name: POST_DETAILS_CHANGED,
                    data: { ...updatedPosts[postIndex] },
                });
            })
            .catch(catchError);
    };

    const cancelActionInProgress = () => {
        setPostActionInProgress(undefined);
    };

    useInterruptableAction(
        postActionInProgress,
        () =>
            performReactionOnPost(
                postActionInProgress.reactionTypeName,
                postActionInProgress.action,
                postActionInProgress.postId,
            ),
        cancelActionInProgress,
    );

    const isInsidePostDetailsPage =
        POST_LIST_TYPES[listType] === POST_LIST_TYPES.POST_DETAILS ||
        POST_LIST_TYPES[listType] === POST_LIST_TYPES.POST_DETAILS_FULLSCREEN;

    const commentButtonClicked = useCallback(
        (e: React.MouseEvent<HTMLElement>, id: number) => {
            isInsidePostDetailsPage
                ? openCommentInputModal?.(CommentFormModeEnum.TEXT)
                : openPostDetailsPage?.(e, id);
        },
        [isInsidePostDetailsPage, openCommentInputModal, openPostDetailsPage],
    );

    function onAvatarClick(user: UserType) {
        publish({ name: SHOW_AVATAR_MODAL_EVENT, user: user });
    }
    let keyForNonGamePosts = 0;

    const { factory } = usePostComponentFactory();

    return (
        <>
            {selectedComment?.commentContent != null && (
                <CommentOptionsModal {...commentOptionsModalProps} />
            )}
            {selectedPost && (
                <PostOptionsModal
                    open={isPostOptionsModalOpen}
                    setOpen={setIsPostOptionsModalOpen}
                    selectedPost={selectedPost}
                    onEditOptionClickHandler={onPostEdit}
                    modalCurrentAction={modalCurrentAction}
                    setModalCurrentAction={setModalCurrentAction}
                    handleCommentInputFocus={commentButtonClicked}
                />
            )}
            <>
                {postList.map((post: PostType, key) => {
                    if (post.postData.type !== POST_TYPES.TIPP_GAME) {
                        keyForNonGamePosts++;
                    }
                    const postOptions: PostComponentOptionsType = {
                        id: post.id,
                        data: post,
                        showGroupName: false,
                        postActions: {
                            onAvatarClick: onAvatarClick,
                            setSelectedPost: setSelectedPost,
                            setIsPostOptionsModalOpen:
                                setIsPostOptionsModalOpen,
                            onCommentButtonClick: commentButtonClicked,
                            onCardContentClickHandler: !isInsidePostDetailsPage
                                ? onPostCardClick
                                : undefined,
                            handleCommentPreviewOptionsClicked:
                                handleCommentPreviewOptionsClicked,
                            handleCommentPreviewReportClicked:
                                handleCommentPreviewReportClicked,
                            setModalCurrentAction: setModalCurrentAction,
                            showGroupCoverImage: showGroupCoverImage,
                            isInsidePostDetails: isInsidePostDetailsPage,
                            updateFromServer: updatePostFromServer,
                            createReactOnPreviewComment,
                            createReactOnPost,
                            setInitialPostLocation,
                            openPostFormHandler,
                            setHeaderComponent,
                            handleCloseForHeaderComponent,
                            autoPlayVideo,
                            showCommentTeaser: keyForNonGamePosts % 4 === 0,
                        },
                        listType,
                        scrollableContainerRef,
                    };
                    const PostComponent = factory(post);
                    if (
                        !isInsidePostDetailsPage &&
                        onPostCardClick &&
                        groupId
                    ) {
                        if (key === 4) {
                            return (
                                <Fragment key={key}>
                                    <VideoSwiperContainer
                                        groupId={groupId.toString()}
                                        onPostCardClick={onPostCardClick}
                                    />
                                    <PostComponent
                                        key={post.id}
                                        {...postOptions}
                                    />
                                </Fragment>
                            );
                        } else if (
                            key === 9 ||
                            (postList.length < 10 &&
                                key === postList.length - 1)
                        ) {
                            return (
                                <Fragment key={key}>
                                    <MediaSwiperContainer
                                        onPostCardClick={onPostCardClick}
                                    />
                                    <PostComponent
                                        key={post.id}
                                        {...postOptions}
                                    />
                                </Fragment>
                            );
                        }
                    }
                    return <PostComponent key={post.id} {...postOptions} />;
                })}
            </>
            <ManualLoadingInfiniteScroller
                infiniteRef={infiniteRef}
                loadMore={dataLoader}
                isLoading={isLoading}
                showLoadMoreButton={showLoadMoreButton}
                isInitLoadingDone={isInitLoadingDone}
                hasNextPage={hasNextPage}
                loadingMoreButtonText={loadingMoreButtonText}
                loadMoreMargin={"0 0 32px 0"}
                autoLoadDataAfterClick={autoLoadDataAfterClick}
                errorHappened={!!error}
            />
            {!isInsidePostDetailsPage && (isLoading || !isInitLoadingDone) && (
                <Box>
                    <SkeletonPost key={1} />
                    <SkeletonPost key={2} />
                    <SkeletonPost key={3} />
                    <SkeletonPost key={4} />
                    <SkeletonPost key={5} />
                    <SkeletonPost key={6} />
                    <SkeletonPost key={7} />
                    <SkeletonPost key={8} />
                    <SkeletonPost key={9} />
                    <SkeletonPost key={10} />
                </Box>
            )}
        </>
    );
}
